import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * OptimizedImage Component
 * 
 * A performance-optimized image component with:
 * - Lazy loading using Intersection Observer
 * - Fallback image support
 * - Loading state with placeholder
 * - Error handling with retry
 * - Fade-in animation
 */
const OptimizedImage = ({
    src,
    alt,
    fallback = '/images/placeholder.jpg',
    className = '',
    style = {},
    loading = 'lazy',
    threshold = 0.1,
}) => {
    const [imageSrc, setImageSrc] = useState(loading === 'eager' ? src : null);
    const [imageError, setImageError] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const imgRef = useRef(null);
    const observerRef = useRef(null);

    useEffect(() => {
        // If eager loading, load immediately
        if (loading === 'eager') {
            setImageSrc(src);
            return;
        }

        // Set up Intersection Observer for lazy loading
        const options = {
            root: null,
            rootMargin: '50px', // Load 50px before entering viewport
            threshold: threshold,
        };

        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setImageSrc(src);
                    // Disconnect after loading
                    if (observerRef.current && imgRef.current) {
                        observerRef.current.unobserve(imgRef.current);
                    }
                }
            });
        }, options);

        if (imgRef.current) {
            observerRef.current.observe(imgRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [src, loading, threshold]);

    const handleLoad = () => {
        setIsLoaded(true);
        setImageError(false);
    };

    const handleError = () => {
        console.warn(`Failed to load image: ${imageSrc}`);
        if (imageSrc !== fallback) {
            setImageSrc(fallback);
            setImageError(true);
        }
    };

    return (
        <div
            ref={imgRef}
            className={`relative overflow-hidden ${className}`}
            style={style}
        >
            {/* Placeholder while loading */}
            {!isLoaded && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse" />
            )}

            {/* Actual Image */}
            {imageSrc && (
                <img
                    src={imageSrc}
                    alt={alt}
                    onLoad={handleLoad}
                    onError={handleError}
                    className={`w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                    style={{ objectFit: style.objectFit || 'cover' }}
                />
            )}
        </div>
    );
};

OptimizedImage.propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    fallback: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    loading: PropTypes.oneOf(['lazy', 'eager']),
    threshold: PropTypes.number,
};

export default OptimizedImage;
